import type { ReactElement } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { isUuid } from "@/lib/parseUuidListParam";

export function DailyLogFormRedirect(): ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`/projects/${projectId}/project-diary-entries?create=1`} replace />;
}

export function DailyLogEditRedirect(): ReactElement {
  const { projectId, projectDiaryEntryId } = useParams<{
    projectId: string;
    projectDiaryEntryId: string;
  }>();
  return (
    <Navigate
      to={`/projects/${projectId}/project-diary-entries?edit=${projectDiaryEntryId}`}
      replace
    />
  );
}

export function ProjectDiaryEntryFormNewRedirect(): ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId || !isUuid(projectId)) {
    return <Navigate to="/projects" replace />;
  }
  return <Navigate to={`/projects/${projectId}/project-diary-entries?create=1`} replace />;
}

export function ProjectDiaryEntryFormEditRedirect(): ReactElement {
  const { projectId, projectDiaryEntryId } = useParams<{
    projectId: string;
    projectDiaryEntryId: string;
  }>();
  if (!projectId || !isUuid(projectId)) {
    return <Navigate to="/projects" replace />;
  }
  if (!projectDiaryEntryId || !isUuid(projectDiaryEntryId)) {
    return <Navigate to={`/projects/${projectId}/project-diary-entries`} replace />;
  }
  return (
    <Navigate
      to={`/projects/${projectId}/project-diary-entries?edit=${projectDiaryEntryId}`}
      replace
    />
  );
}

export function ProjectEditRedirect(): ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId || !isUuid(projectId)) {
    return <Navigate to="/projects" replace />;
  }
  return <Navigate to={`/projects?edit=${projectId}`} replace />;
}

export function ProjectServiceFrontEditRedirect(): ReactElement {
  const { projectId, projectServiceFrontId } = useParams<{
    projectId: string;
    projectServiceFrontId: string;
  }>();
  if (!projectId || !isUuid(projectId)) {
    return <Navigate to="/projects" replace />;
  }
  if (!projectServiceFrontId || !isUuid(projectServiceFrontId)) {
    return <Navigate to={`/projects/${projectId}/project-service-fronts`} replace />;
  }
  return (
    <Navigate
      to={`/projects/${projectId}/project-service-fronts?edit=${projectServiceFrontId}`}
      replace
    />
  );
}

export function ProjectMaterialsAddRedirect(): ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId || !isUuid(projectId)) {
    return <Navigate to="/projects" replace />;
  }
  return <Navigate to={`/projects/${projectId}/materials?create=1`} replace />;
}

export function ProjectEquipmentsAddRedirect(): ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId || !isUuid(projectId)) {
    return <Navigate to="/projects" replace />;
  }
  return <Navigate to={`/projects/${projectId}/equipments?create=1`} replace />;
}

function ProjectListCreateRedirect({ segment }: { segment: string }): ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId || !isUuid(projectId)) {
    return <Navigate to="/projects" replace />;
  }
  return <Navigate to={`/projects/${projectId}/${segment}?create=1`} replace />;
}

function ProjectListEditRedirect({
  segment,
  idParam,
  queryKey = "edit",
}: {
  segment: string;
  idParam: string;
  queryKey?: string;
}): ReactElement {
  const params = useParams();
  const projectId = params.projectId;
  const id = params[idParam];
  if (!projectId || !isUuid(projectId)) {
    return <Navigate to="/projects" replace />;
  }
  const listPath = `/projects/${projectId}/${segment}`;
  if (!id || !isUuid(id)) {
    return <Navigate to={listPath} replace />;
  }
  return <Navigate to={`${listPath}?${queryKey}=${id}`} replace />;
}

export function ProjectBudgetCreateRedirect(): ReactElement {
  return <ProjectListCreateRedirect segment="project-budgets" />;
}

export function ProjectBudgetEditRedirect(): ReactElement {
  return <ProjectListEditRedirect segment="project-budgets" idParam="budgetId" />;
}

export function ProjectBudgetNewVersionRedirect(): ReactElement {
  return (
    <ProjectListEditRedirect segment="project-budgets" idParam="budgetId" queryKey="newVersion" />
  );
}

export function ProjectClientBudgetCreateRedirect(): ReactElement {
  return <ProjectListCreateRedirect segment="client-budgets" />;
}

export function ProjectClientBudgetEditRedirect(): ReactElement {
  return <ProjectListEditRedirect segment="client-budgets" idParam="clientBudgetId" />;
}

export function ProjectClientBudgetNewVersionRedirect(): ReactElement {
  return (
    <ProjectListEditRedirect
      segment="client-budgets"
      idParam="clientBudgetId"
      queryKey="newVersion"
    />
  );
}

export function ProjectDocumentCreateRedirect(): ReactElement {
  return <ProjectListCreateRedirect segment="documents" />;
}

export function ProjectDocumentEditRedirect(): ReactElement {
  return <ProjectListEditRedirect segment="documents" idParam="documentId" />;
}

export function ProjectDocumentNewVersionRedirect(): ReactElement {
  return <ProjectListEditRedirect segment="documents" idParam="documentId" queryKey="newVersion" />;
}

export function PurchaseRequestNewRedirect(): ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  if (!projectId || !isUuid(projectId)) {
    return <Navigate to="/projects" replace />;
  }
  const next = new URLSearchParams(searchParams);
  next.set("create", "1");
  return <Navigate to={`/projects/${projectId}/purchase-requests?${next.toString()}`} replace />;
}

export function PurchaseRequestEditRedirect(): ReactElement {
  return <ProjectListEditRedirect segment="purchase-requests" idParam="purchaseRequestId" />;
}

export function ProjectPayablesCreateRedirect(): ReactElement {
  return <ProjectListCreateRedirect segment="payables" />;
}

export function ProjectPayablesEditRedirect(): ReactElement {
  return <ProjectListEditRedirect segment="payables" idParam="id" />;
}

export function ProjectReceivablesCreateRedirect(): ReactElement {
  return <ProjectListCreateRedirect segment="receivables" />;
}

export function ProjectReceivablesEditRedirect(): ReactElement {
  return <ProjectListEditRedirect segment="receivables" idParam="id" />;
}
